/**
 * What 3 Ingredients - Recipe Engine
 * Generates minimalist 3-ingredient recipes targeting Google's Gemini 3.5 Flash Lite model
 * with strict unit system rules (Imperial vs Metric) and anti-duplication blacklist.
 */

export function getGeminiApiKey() {
  return (import.meta.env && import.meta.env.VITE_GEMINI_API_KEY) || '';
}

/**
 * Generates a recipe using Google Gemini 3.5 Flash Lite API
 * @param {string[]} mainIngredients 
 * @param {string[]} utilityIngredients 
 * @param {string} region 
 * @param {number} servings
 * @param {string[]} seenTitles - Array of previously generated recipe titles for this session
 * @param {string} unitSystem - 'imperial' or 'metric'
 * @returns {Promise<object>} Recipe object
 */
export async function generateRecipe(mainIngredients = [], utilityIngredients = [], region = 'all', servings = 2, seenTitles = [], unitSystem = 'imperial') {
  const apiKey = getGeminiApiKey();

  // If no API key configured, return fallback recipe
  if (!apiKey) {
    return generateFallbackRecipe(mainIngredients, region, servings, unitSystem);
  }

  const regionPrompt = region !== 'all' ? `Cuisine style: ${region}.` : 'Cuisine style: Global.';
  const userIngredientsText = mainIngredients.length > 0
    ? mainIngredients.join(', ')
    : 'Chef choice ingredients';

  const historyBlacklistPrompt = seenTitles && seenTitles.length > 0
    ? `STRICT ANTI-DUPLICATION RULE: The user has ALREADY seen these recipes in this session: [ ${seenTitles.map(t => `"${t}"`).join(', ')} ]. Do NOT generate any of those titles or simple variations. You MUST create a completely NEW, unique recipe with a distinct cooking technique, flavor profile, or presentation.`
    : '';

  const unitPrompt = unitSystem === 'metric'
    ? `MEASUREMENT SYSTEM RULE: Use METRIC units exclusively (grams, kg, ml, liters, tbsp, tsp) for ALL ingredient quantities and step descriptions.`
    : `MEASUREMENT SYSTEM RULE: Use IMPERIAL units exclusively (ounces, lbs, cups, tbsp, tsp, fluid oz) for ALL ingredient quantities and step descriptions.`;

  const systemPrompt = `You are an expert chef creating a minimalist 3-main-ingredient recipe for ${servings} serving(s).
Target inputs: ${userIngredientsText}.
${regionPrompt}
${unitPrompt}
${historyBlacklistPrompt}

STRICT RULES:
1. Include EXACTLY 3 MAIN ingredients in mainIngredients. If fewer than 3 were given, choose delicious complementary main ingredients that pair perfectly.
2. Pantry utility staples (salt, black pepper, olive oil, vegetable oil, butter, water, garlic powder, soy sauce, vinegar, sugar) DO NOT count toward the 3-main-ingredient limit. Place utility staples inside utilityIngredients.
3. In mainIngredientQuantities, specify exact quantities and units for ALL 3 main ingredients for ${servings} serving(s) using the requested ${unitSystem.toUpperCase()} unit system.
4. Every step in "steps" MUST be clear, logical, and specific to the actual ingredient preparation. NEVER give generic steps like "wash peanut butter".
5. Make instructions easy to follow for home cooks.`;

  // Standard OpenAPI Subset Schema for Gemini API Structured Outputs
  const schema = {
    type: "OBJECT",
    properties: {
      title: { type: "STRING", description: "Appetizing culinary recipe title" },
      tagline: { type: "STRING", description: "Short 1-sentence mouth-watering description" },
      region: { type: "STRING", description: "Region name e.g. North American, Mediterranean, East Asian, etc." },
      mainIngredients: {
        type: "ARRAY",
        items: { type: "STRING" },
        description: "Array of exactly 3 main ingredients"
      },
      mainIngredientQuantities: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            name: { type: "STRING" },
            qty: { type: "NUMBER" },
            unit: { type: "STRING" }
          },
          required: ["name", "qty", "unit"]
        },
        description: "Exact quantities for all 3 main ingredients for the specified servings"
      },
      utilityIngredients: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            name: { type: "STRING" },
            qty: { type: "NUMBER" },
            unit: { type: "STRING" }
          },
          required: ["name", "qty", "unit"]
        },
        description: "Exempt pantry staples (oil, salt, pepper, water, butter, etc.)"
      },
      prepTime: { type: "STRING", description: "e.g. 5 mins" },
      cookTime: { type: "STRING", description: "e.g. 15 mins" },
      steps: {
        type: "ARRAY",
        items: { type: "STRING" },
        description: "4 to 6 detailed step-by-step cooking instructions"
      },
      chefTip: { type: "STRING", description: "Useful chef tip for best results" }
    },
    required: ["title", "tagline", "region", "mainIngredients", "mainIngredientQuantities", "utilityIngredients", "prepTime", "cookTime", "steps", "chefTip"]
  };

  const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash-lite:generateContent?key=${apiKey}`;
  const temp = (seenTitles && seenTitles.length > 0) ? 0.95 : 0.7;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: systemPrompt }] }],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: schema,
          temperature: temp
        }
      })
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      console.warn('Recipe API request failed:', response.status, errData);
      return generateFallbackRecipe(mainIngredients, region, servings, unitSystem);
    }

    const data = await response.json();
    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text;
    
    if (!rawText) {
      return generateFallbackRecipe(mainIngredients, region, servings, unitSystem);
    }

    const parsed = JSON.parse(rawText);
    const mains = parsed.mainIngredients || mainIngredients;

    // Transform array of mainIngredientQuantities to map
    const baseQuantities = {};
    if (parsed.mainIngredientQuantities && Array.isArray(parsed.mainIngredientQuantities)) {
      parsed.mainIngredientQuantities.forEach((item, idx) => {
        const keyName = item.name ? item.name.toLowerCase() : (mains[idx] || `ingredient_${idx}`).toLowerCase();
        baseQuantities[keyName] = { qty: item.qty || 1, unit: item.unit || 'portion' };
      });
    }

    // Ensure all 3 mains have an entry in baseQuantities
    mains.forEach((m, idx) => {
      const lowerM = m.toLowerCase();
      if (!baseQuantities[lowerM]) {
        const match = Object.keys(baseQuantities).find(k => k.includes(lowerM) || lowerM.includes(k));
        if (match) {
          baseQuantities[lowerM] = baseQuantities[match];
        } else {
          const qtyObj = parsed.mainIngredientQuantities?.[idx];
          baseQuantities[lowerM] = {
            qty: qtyObj?.qty || (lowerM.includes('egg') ? 2 : (unitSystem === 'metric' ? 250 : 8)),
            unit: qtyObj?.unit || (lowerM.includes('egg') ? 'large' : (unitSystem === 'metric' ? 'g' : 'oz'))
          };
        }
      }
    });

    return {
      id: 'rec-' + Date.now(),
      title: parsed.title,
      tagline: parsed.tagline,
      region: parsed.region || region,
      unitSystem: unitSystem,
      mainIngredients: mains,
      baseQuantities: baseQuantities,
      utilityIngredients: parsed.utilityIngredients || [],
      prepTime: parsed.prepTime || '5 mins',
      cookTime: parsed.cookTime || '15 mins',
      servings: servings,
      steps: parsed.steps || [],
      chefTip: parsed.chefTip || ''
    };
  } catch (error) {
    console.warn('Using fallback recipe generator:', error);
    return generateFallbackRecipe(mainIngredients, region, servings, unitSystem);
  }
}

/**
 * Gets a random 3-ingredient recipe
 */
export async function getRandomRecipe(region = 'all', servings = 2, unitSystem = 'imperial') {
  const randomCombos = [
    ['salmon fillets', 'lemon', 'garlic'],
    ['chicken breast', 'salsa', 'monterey jack cheese'],
    ['avocado', 'fresh mozzarella', 'cherry tomatoes'],
    ['eggs', 'spinach', 'cheddar cheese'],
    ['banana', 'rolled oats', 'peanut butter'],
    ['shrimp', 'garlic', 'butter'],
    ['pasta', 'pecorino cheese', 'black pepper'],
    ['sirloin steak', 'garlic', 'parsley']
  ];
  const chosen = randomCombos[Math.floor(Math.random() * randomCombos.length)];
  return generateRecipe(chosen, [], region, servings, [], unitSystem);
}

/**
 * Fallback recipe generator
 */
function generateFallbackRecipe(ingredients, region, servings = 2, unitSystem = 'imperial') {
  const mains = ingredients.length > 0 ? ingredients.slice(0, 3) : ['chicken breast', 'lemon', 'garlic'];
  while (mains.length < 3) {
    const complements = ['spinach', 'cheddar cheese', 'cherry tomatoes', 'rice', 'broccoli'];
    mains.push(complements[mains.length % complements.length]);
  }

  const title = mains.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(' & ') + ' Skillet';

  const baseQuantities = {};
  mains.forEach(ing => {
    baseQuantities[ing.toLowerCase()] = {
      qty: ing.includes('egg') ? 2 : (unitSystem === 'metric' ? 250 : 8),
      unit: ing.includes('egg') ? 'large' : (unitSystem === 'metric' ? 'g' : 'oz')
    };
  });

  return {
    id: 'fb-' + Date.now(),
    title: title,
    tagline: `A quick 3-ingredient dish combining ${mains.join(', ')}.`,
    region: region === 'all' ? 'global' : region,
    unitSystem: unitSystem,
    mainIngredients: mains,
    baseQuantities: baseQuantities,
    utilityIngredients: [
      { name: 'olive oil', qty: unitSystem === 'metric' ? 15 : 1, unit: unitSystem === 'metric' ? 'ml' : 'tbsp' },
      { name: 'salt', qty: 0.5, unit: 'tsp' },
      { name: 'black pepper', qty: 0.25, unit: 'tsp' }
    ],
    prepTime: '5 mins',
    cookTime: '12 mins',
    servings: servings,
    steps: [
      `Prep the ${mains.join(', ')} by rinsing and slicing into uniform pieces.`,
      `Heat olive oil in a pan over medium heat. Season with a pinch of salt and pepper.`,
      `Add ${mains[0]} and cook for 4-5 minutes until seared.`,
      `Incorporate ${mains.slice(1).join(' and ')} into the pan and toss until cooked through and hot.`,
      `Plate warm and enjoy!`
    ],
    chefTip: 'Keep salt, black pepper, and good olive oil in your pantry for instant flavor.'
  };
}
